'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { Trash2, Edit, Shield, RefreshCw, Zap, ShieldAlert, Copy, Magnet, AlertTriangle, Archive, EyeOff } from 'lucide-react';
import { getIntegrationImage } from '@/lib/utils/integration-utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePaymentFlowWithProcessors, PaymentFlowItemWithProcessors } from '@/lib/api/paymentFlows';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useState } from 'react';
import { TpaLiveStatus } from '@/components/tagadapay/tpa-live-status';
import { PaymentFlowDeleteDialog } from '../components/payment-flow-delete-dialog';
import { PaymentFlowDiagram } from './components/payment-flow-diagram';
import { StatCard } from './components/stat-card';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { PAYMENT_FLOW_SUB_NAVIGATION_IDS, PaymentFlowSubNavigation } from './components/sub-menu-navigation';
import { BackButton } from '@/components/ui/BackButton';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { formatDate } from '@/lib/format';
import { RoutingPinsSummary } from '@/components/form/payment-flow/RoutingPinsSummary';
import { toast } from 'react-hot-toast';

// Helper functions for feature detection
const isSmartRetryEnabledAndValid = (flow: { rebillSettings?: { customRetries?: unknown[] } | null }) => {
  const rebillSettings = flow.rebillSettings;
  if (!rebillSettings) return false;
  if (!rebillSettings.customRetries || rebillSettings.customRetries.length === 0) return false;
  return true;
};

const isAbuseDetectionEnabledAndValid = (flow: { abuseDetectionConfig?: { enabled?: boolean } | null }) => {
  const abuseDetectionConfig = flow.abuseDetectionConfig;
  if (!abuseDetectionConfig) return false;
  return abuseDetectionConfig.enabled === true;
};

const PaymentFlowDetailsPageInner = () => {
  const { t } = useTranslation(['paymentFlows', 'common']);
  const { flowId } = useParams();

  if (!flowId) {
    throw new Error('Flow ID is required');
  }

  const {
    data: paymentFlow,
    isLoading,
    error,
  } = usePaymentFlowWithProcessors(flowId);

  if (isLoading) {
    return <PaymentFlowDetailsLoading />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!paymentFlow) {
    return (
      <div className="container mx-auto py-8">
        <p className="text-red-500">
          {t('paymentFlows:details.not_found')}
        </p>
      </div>
    );
  }

  return <PaymentFlowDetailContent paymentFlow={paymentFlow} />;
};

const PaymentFlowDetailsLoading = () => {
  return (
    <div className="container mx-auto space-y-6 py-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

interface PaymentFlowDetailContentProps {
  paymentFlow: ReturnType<typeof usePaymentFlowWithProcessors>['data'];
}

const PaymentFlowDetailContent = ({ paymentFlow }: PaymentFlowDetailContentProps) => {
  const { t } = useTranslation(['paymentFlows', 'common']);
  const { orgRoutes, navigate } = useOrgNavigate();
  const [flowToDelete, setFlowToDelete] = useState<PaymentFlowItemWithProcessors | null>(null);

  if (!paymentFlow) return null;

  // Feature detection
  const smartRetryEnabled = isSmartRetryEnabledAndValid(paymentFlow);
  const abuseDetectionEnabled = isAbuseDetectionEnabledAndValid(paymentFlow);

  const handleEdit = () => {
    navigate(orgRoutes.PAYMENT_FLOW_EDIT(paymentFlow.id));
  };

  const handleDelete = () => {
    // Cast to PaymentFlowItemWithProcessors for the delete dialog
    setFlowToDelete(paymentFlow as PaymentFlowItemWithProcessors);
  };

  const handleDeleteClose = () => {
    setFlowToDelete(null);
    navigate(orgRoutes.PAYMENT_FLOWS());
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(paymentFlow.id);
    toast.success(t('common:copied'));
  };

  const activeProcessors =
    paymentFlow.processorConfigs?.filter((c: { disabled?: boolean }) => !c.disabled) ?? [];
  const fallbackProcessors = paymentFlow.fallbackProcessorConfigs ?? [];
  const totalWeight = activeProcessors.reduce(
    (sum: number, c: { weight?: number }) => sum + (c.weight || 0),
    0,
  );

  // Check for archived or disabled processors
  const archivedPrimaryProcessors = activeProcessors.filter(c => c.processor?.archivedAt);
  const archivedFallbackProcessors = fallbackProcessors.filter(c => c.processor?.archivedAt);
  const disabledPrimaryProcessors = activeProcessors.filter(c => !c.processor?.archivedAt && c.processor?.enabled === false);
  const disabledFallbackProcessors = fallbackProcessors.filter(c => !c.processor?.archivedAt && c.processor?.enabled === false);
  const hasArchivedProcessors = archivedPrimaryProcessors.length > 0 || archivedFallbackProcessors.length > 0;
  const hasDisabledProcessors = disabledPrimaryProcessors.length > 0 || disabledFallbackProcessors.length > 0;

  return (
    <>
      <PaymentFlowSubNavigation activeId={PAYMENT_FLOW_SUB_NAVIGATION_IDS.DETAILS} />
      <div className="container mx-auto bg-background py-6">
        <div className="flex flex-col space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-4">
              <BackButton to={orgRoutes.PAYMENT_FLOWS()} />
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{paymentFlow.name}</h1>
                {/* Feature badges */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground text-sm">
                    {paymentFlow.strategy === 'weighted'
                      ? t('paymentFlows:badges.custom_routing')
                      : t('paymentFlows:badges.auto_routing')}
                  </span>
                  {paymentFlow.threeDsEnabled && (
                    <Badge variant="secondary" className="gap-1 border-0 bg-blue-100 px-2 py-0.5 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                      <Shield className="size-3" />
                      {t('paymentFlows:badges.3ds')}
                    </Badge>
                  )}
                  {paymentFlow.fallbackMode && (
                    <Badge variant="secondary" className="gap-1 border-0 bg-amber-100 px-2 py-0.5 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                      <RefreshCw className="size-3" />
                      {t('paymentFlows:badges.fallback_retries', { count: paymentFlow.maxFallbackRetries ?? 0 })}
                    </Badge>
                  )}
                  {paymentFlow.stickyProcessorEnabled && (
                    <Badge variant="secondary" className="gap-1 border-0 bg-indigo-100 px-2 py-0.5 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                      <Magnet className="size-3" />
                      {t('paymentFlows:badges.sticky')}
                    </Badge>
                  )}
                  {smartRetryEnabled && (
                    <Badge variant="secondary" className="gap-1 border-0 bg-purple-100 px-2 py-0.5 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                      <Zap className="size-3" />
                      {t('paymentFlows:badges.smart_retry')}
                    </Badge>
                  )}
                  {abuseDetectionEnabled && (
                    <Badge variant="secondary" className="gap-1 border-0 bg-rose-100 px-2 py-0.5 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                      <ShieldAlert className="size-3" />
                      {t('paymentFlows:badges.fraud_protection')}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleCopyId} className="text-muted-foreground">
                <Copy className="mr-2 size-4" />
                {t('common:copy_id')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Edit className="mr-2 size-4" />
                {t('common:edit', { ns: 'common' })}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDelete}>
                <Trash2 className="mr-2 size-4" />
                {t('common:delete', { ns: 'common' })}
              </Button>
            </div>
          </div>

          {/* Warning banner for archived processors */}
          {hasArchivedProcessors && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/50">
              <AlertTriangle className="mt-0.5 size-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  {t('paymentFlows:warnings.archived_processors_title')}
                </p>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  {t('paymentFlows:warnings.archived_processors_description')}
                </p>
              </div>
            </div>
          )}

          {/* Warning banner for disabled processors */}
          {hasDisabledProcessors && (
            <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-950/50">
              <AlertTriangle className="mt-0.5 size-5 flex-shrink-0 text-orange-600 dark:text-orange-400" />
              <div>
                <p className="font-medium text-orange-800 dark:text-orange-200">
                  {t('paymentFlows:warnings.disabled_processors_title')}
                </p>
                <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                  {t('paymentFlows:warnings.disabled_processors_description')}
                </p>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            {/* Main content - 2/3 width */}
            <div className="space-y-6 lg:col-span-2">
              {/* Stats Cards */}
              <div className="grid gap-4 md:grid-cols-2">
                <StatCard
                  label={t('paymentFlows:details.stats.routing_strategy.label')}
                  value={
                    paymentFlow.pickProcessorStrategy === 'weighted'
                      ? t('paymentFlows:badges.custom_routing')
                      : t('paymentFlows:badges.auto_routing')
                  }
                  description={
                    paymentFlow.pickProcessorStrategy === 'weighted'
                      ? t('paymentFlows:details.stats.routing_strategy.weighted')
                      : t('paymentFlows:details.stats.routing_strategy.automatic')
                  }
                />
                <StatCard
                  label={t('paymentFlows:details.stats.active_processors.label')}
                  value={activeProcessors.length}
                  description={t('paymentFlows:details.stats.active_processors.description', {
                    total: paymentFlow.processorConfigs?.length ?? 0,
                  })}
                />
                <StatCard
                  label={t('paymentFlows:details.stats.fallback_queue.label')}
                  value={paymentFlow.fallbackMode ? fallbackProcessors.length : 0}
                  description={
                    paymentFlow.fallbackMode
                      ? t('paymentFlows:details.stats.fallback_queue.description', {
                          max: paymentFlow.maxFallbackRetries,
                        })
                      : t('paymentFlows:details.stats.fallback_queue.disabled')
                  }
                />
                <StatCard
                  label={t('paymentFlows:details.stats.weight_balance.label')}
                  value={`${totalWeight}%`}
                  description={
                    totalWeight === 100
                      ? t('paymentFlows:details.stats.weight_balance.perfectly_balanced')
                      : totalWeight < 100
                        ? t('paymentFlows:details.stats.weight_balance.under_allocated')
                        : t('paymentFlows:details.stats.weight_balance.over_allocated')
                  }
                />
              </div>

              {/* Primary Processors */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t('paymentFlows:details.sections.primary_routing')}
                  </CardTitle>
                  <CardDescription>
                    {t('paymentFlows:details.sections.primary_routing_description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {activeProcessors.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('paymentFlows:details.sections.no_active_processors')}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {activeProcessors.map(config => {
                        const isArchived = !!config.processor?.archivedAt;
                        const isDisabled = !isArchived && config.processor?.enabled === false;
                        const hasIssue = isArchived || isDisabled;
                        return (
                          <div
                            key={config.processorId}
                            className={`flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/50 ${isArchived ? 'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30' : ''} ${isDisabled ? 'border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/30' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={getIntegrationImage(config.processor?.type ?? '')}
                                alt={config.processor?.type ?? 'Unknown'}
                                className={`size-8 rounded-md object-contain ${hasIssue ? 'opacity-50 grayscale' : ''}`}
                              />
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className={`font-medium ${hasIssue ? 'text-muted-foreground' : ''}`}>
                                    {config.processor?.name ??
                                      t('paymentFlows:details.sections.processor_unknown')}
                                  </p>
                                  {isArchived && (
                                    <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                                      <Archive className="size-3" />
                                      {t('paymentFlows:details.processor_status.archived')}
                                    </Badge>
                                  )}
                                  {isDisabled && (
                                    <Badge variant="outline" className="gap-1 border-orange-300 bg-orange-100 text-orange-700 dark:border-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                                      <EyeOff className="size-3" />
                                      {t('paymentFlows:details.processor_status.disabled')}
                                    </Badge>
                                  )}
                                </div>
                                {config.processor?.tagadapayAccountId ? (
                                  <TpaLiveStatus
                                    tpaId={config.processor.tagadapayAccountId}
                                    status={config.processor.tagadapayStatus}
                                    mode={config.processor.tagadapayMode}
                                    className="mt-0.5"
                                  />
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    {config.processor?.type}
                                  </p>
                                )}
                                <RoutingPinsSummary
                                  config={config}
                                  pickProcessorStrategy={paymentFlow.pickProcessorStrategy}
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {config.weight && config.weight > 0 && (
                                <Badge variant="secondary">{config.weight}%</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Fallback Processors */}
              {paymentFlow.fallbackMode && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {t('paymentFlows:details.sections.fallback_sequence')}
                    </CardTitle>
                    <CardDescription>
                      {t('paymentFlows:details.sections.fallback_sequence_description')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {fallbackProcessors.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t('paymentFlows:details.sections.no_fallback_processors')}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {fallbackProcessors
                          .slice(0, paymentFlow.maxFallbackRetries ?? 0)
                          .map((config, index) => {
                            const isArchived = !!config.processor?.archivedAt;
                            const isDisabled = !isArchived && config.processor?.enabled === false;
                            const hasIssue = isArchived || isDisabled;
                            return (
                              <div
                                key={config.processorId}
                                className={`flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/50 ${isArchived ? 'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30' : ''} ${isDisabled ? 'border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/30' : ''}`}
                              >
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline" className="px-2 text-xs">#{index + 1}</Badge>
                                  <img
                                    src={getIntegrationImage(config.processor?.type ?? '')}
                                    alt={config.processor?.type ?? 'Unknown'}
                                    className={`size-8 rounded-md object-contain ${hasIssue ? 'opacity-50 grayscale' : ''}`}
                                  />
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className={`font-medium ${hasIssue ? 'text-muted-foreground' : ''}`}>
                                        {config.processor?.name ??
                                          t('paymentFlows:details.sections.processor_unknown')}
                                      </p>
                                      {isArchived && (
                                        <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                                          <Archive className="size-3" />
                                          {t('paymentFlows:details.processor_status.archived')}
                                        </Badge>
                                      )}
                                      {isDisabled && (
                                        <Badge variant="outline" className="gap-1 border-orange-300 bg-orange-100 text-orange-700 dark:border-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                                          <EyeOff className="size-3" />
                                          {t('paymentFlows:details.processor_status.disabled')}
                                        </Badge>
                                      )}
                                    </div>
                                    {config.processor?.tagadapayAccountId ? (
                                      <TpaLiveStatus
                                        tpaId={config.processor.tagadapayAccountId}
                                        status={config.processor.tagadapayStatus}
                                        mode={config.processor.tagadapayMode}
                                        className="mt-0.5"
                                      />
                                    ) : (
                                      <p className="text-xs text-muted-foreground">
                                        {config.processor?.type}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Flow Diagram */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t('paymentFlows:details.sections.flow_visualization')}
                  </CardTitle>
                  <CardDescription>
                    {t('paymentFlows:details.sections.flow_visualization_description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PaymentFlowDiagram paymentFlow={paymentFlow} />
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - 1/3 width */}
            <div className="space-y-6 lg:sticky lg:top-0 lg:h-fit">
              {/* Configuration Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {t('paymentFlows:details.configuration')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('paymentFlows:details.config.routing')}</span>
                    <span className="font-medium capitalize">{paymentFlow.pickProcessorStrategy}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('paymentFlows:badges.3ds')}</span>
                    <span className={paymentFlow.threeDsEnabled ? 'font-medium text-emerald-600' : 'text-muted-foreground'}>
                      {paymentFlow.threeDsEnabled ? t('paymentFlows:details.config.on') : t('paymentFlows:details.config.off')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('paymentFlows:details.config.fallback')}</span>
                    <span className={paymentFlow.fallbackMode ? 'font-medium text-emerald-600' : 'text-muted-foreground'}>
                      {paymentFlow.fallbackMode
                        ? t('paymentFlows:details.config.retries_count', { count: paymentFlow.maxFallbackRetries ?? 0 })
                        : t('paymentFlows:details.config.off')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('paymentFlows:badges.sticky')}</span>
                    <span className={paymentFlow.stickyProcessorEnabled ? 'font-medium text-emerald-600' : 'text-muted-foreground'}>
                      {paymentFlow.stickyProcessorEnabled ? t('paymentFlows:details.config.on') : t('paymentFlows:details.config.off')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('paymentFlows:badges.smart_retry')}</span>
                    <span className={smartRetryEnabled ? 'font-medium text-emerald-600' : 'text-muted-foreground'}>
                      {smartRetryEnabled ? t('paymentFlows:details.config.on') : t('paymentFlows:details.config.off')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('paymentFlows:badges.fraud_protection')}</span>
                    <span className={abuseDetectionEnabled ? 'font-medium text-emerald-600' : 'text-muted-foreground'}>
                      {abuseDetectionEnabled ? t('paymentFlows:details.config.on') : t('paymentFlows:details.config.off')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3">
                    <span className="text-muted-foreground">{t('paymentFlows:details.config.created')}</span>
                    <span className="text-xs">{paymentFlow.createdAt ? formatDate(paymentFlow.createdAt) : '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('paymentFlows:details.config.updated')}</span>
                    <span className="text-xs">{paymentFlow.updatedAt ? formatDate(paymentFlow.updatedAt) : '-'}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <PaymentFlowDeleteDialog
        flow={flowToDelete}
        open={!!flowToDelete}
        onOpenChange={(open) => !open && setFlowToDelete(null)}
        onClose={handleDeleteClose}
      />
    </>
  );
};

export const PaymentFlowDetailsPage = withRequiredPermission(PaymentFlowDetailsPageInner, PERMISSIONS.PROCESSORS_VIEW);
export default PaymentFlowDetailsPage;
